import { useEffect, useState } from 'react';

import { UnAuthAPIName } from 'src/constants';
import { GetVersionResponse } from 'src/entities/GetVersionResponse';
import { API } from 'src/utils/AmplifyApiUtils';
import { getCookie } from 'src/utils/StorageUtils';

const VERSION_POLL_INTERVAL = 5 * 60 * 1000; // poll every 5 minutes
const SUPPRESS_VERSION_CHECK_COOKIE = 'suppress-version-check';

// Module-level singleton state so polling runs once regardless of how many
// components call useTrackAppVersion().
let hasNewVersionGlobal = false;
let polling = false;
const listeners = new Set<(v: boolean) => void>();
const previousVersions: string[] = [];
let timer = 0;

function notify() {
  listeners.forEach((fn) => fn(hasNewVersionGlobal));
}

async function trackVersion() {
  try {
    const result: GetVersionResponse = await API.get(
      UnAuthAPIName,
      '/version',
      {},
    );

    console.info('version response: ', result);
    const { version } = result;
    if (!previousVersions.includes(version)) {
      previousVersions.push(version);
      // Length > 1 means this isn't the initial load — a new version deployed
      if (previousVersions.length > 1) {
        hasNewVersionGlobal = true;
        notify();
      }
    }
  } catch (ex) {
    console.error('error getting version', ex);
  }

  // Guard against stopPolling() having been called while we were awaiting.
  if (polling) {
    timer = window.setTimeout(trackVersion, VERSION_POLL_INTERVAL);
  }
}

function startPolling() {
  if (polling) return;
  if (getCookie(SUPPRESS_VERSION_CHECK_COOKIE)) return;
  polling = true;
  trackVersion();
}

function stopPolling() {
  if (timer) {
    clearTimeout(timer);
    timer = 0;
  }
  polling = false;
}

export const useTrackAppVersion = () => {
  const [hasNewVersion, setHasNewVersion] = useState(hasNewVersionGlobal);

  useEffect(() => {
    listeners.add(setHasNewVersion);
    startPolling();

    return () => {
      listeners.delete(setHasNewVersion);
      if (listeners.size === 0) {
        stopPolling();
      }
    };
  }, []);

  return { hasNewVersion };
};
